import { computed, ref, watch, type Ref } from 'vue';
import type { ChatIntent, ChatTurn, TicketProposal } from '~/utils/agent-chat';
import { canUseTicketCard, latestProposal, ticketDraftValid, ticketPayload } from '~/utils/chat-ticket';

export type TicketCardStatus = 'loading' | 'editing' | 'creating' | 'created' | 'error';

/**
 * Where the text of the card comes from: a proposal of Suge, a proposal run that gave nothing, or
 * an empty chat, where there is nothing to summarize.
 */
export type TicketCardSource = 'suge' | 'failed' | 'empty';

export interface TicketCard {
  status: TicketCardStatus;
  title: string;
  summary: string;
  source: TicketCardSource;
  ticketUid: string | null;
}

interface CreateResult {
  result: { data: { uid: string } } | null;
  error: unknown;
}

/**
 * The escalation card of the chat. The agent never creates a ticket. The browser creates it with
 * the admin token of the user and the existing ticket endpoint, so the normal ticket rules apply.
 */
export function useChatTicketCard(options: {
  conversation: {
    turns: Ref<ChatTurn[]>;
    sessionId: Ref<string | null>;
    ticketUid: Ref<string | null>;
    send: (question: string, intent?: ChatIntent) => Promise<void>;
  };
  isSystemManager: () => boolean;
  /** The shop of the header. */
  shopUid: () => string | null | undefined;
  createTicket: (shopUid: string, title: string, body: string) => Promise<CreateResult>;
  /** `PATCH /v1/sessions/{id}` with the ticket uid, for the badge of the history. */
  recordTicket: (sessionId: string, ticketUid: string) => Promise<void>;
}) {
  const card = ref<TicketCard | null>(null);
  const allowed = computed(() => canUseTicketCard({ isSystemManager: options.isSystemManager() }));
  // The proposal that the card shows. The user changed the draft if the fields differ from it.
  let shown: TicketProposal | null = null;

  const edit = (proposal: TicketProposal | null, source: TicketCardSource) => {
    shown = proposal;
    card.value = {
      status: 'editing',
      title: proposal?.title ?? '',
      summary: proposal?.summary ?? '',
      source,
      ticketUid: null,
    };
  };

  // A proposal can replace only a card that holds no work of the user.
  const replaceable = () => {
    const current = card.value;
    if (current === null || current.status === 'loading') return true;
    return (
      current.status === 'editing' &&
      current.title === (shown?.title ?? '') &&
      current.summary === (shown?.summary ?? '')
    );
  };

  /** The card from the menu item or from a thumbs down. */
  const openCard = async () => {
    if (!allowed.value || card.value?.status === 'loading' || !replaceable()) return;
    const turns = options.conversation.turns.value;
    const existing = latestProposal(turns);
    if (existing) {
      edit(existing, 'suge');
      return;
    }
    if (turns.length === 0) {
      edit(null, 'empty');
      return;
    }
    // Suge gave no proposal yet, so one run with the proposal intent writes the title and the summary.
    card.value = { status: 'loading', title: '', summary: '', source: 'suge', ticketUid: null };
    const loading = card.value;
    await options.conversation.send('', 'ticket_proposal');
    // A new chat or another session replaces the card during the run. The old run then fills nothing.
    if (card.value !== loading) return;
    const proposal = latestProposal(options.conversation.turns.value.slice(turns.length));
    edit(proposal, proposal ? 'suge' : 'failed');
  };

  /** The card from a `ticket_proposal` artifact. */
  const proposalArrived = (proposal: TicketProposal) => {
    if (!allowed.value || !replaceable()) return;
    edit(proposal, 'suge');
  };

  // The proposal of the last turn opens the card: an artifact of an answer, the proposal run of
  // openCard(), or a retry of that run. The watch is sync, so openCard() sees the card that the
  // proposal run filled before its own send() resolves.
  watch(
    () => options.conversation.turns.value.at(-1)?.proposal ?? null,
    (proposal, previous) => {
      if (proposal && proposal !== previous) proposalArrived(proposal);
    },
    { flush: 'sync' },
  );

  const create = async () => {
    const draft = card.value;
    const shopUid = options.shopUid();
    if (!draft || (draft.status !== 'editing' && draft.status !== 'error')) return;
    if (!shopUid || !ticketDraftValid(shopUid, draft)) return;
    const payload = ticketPayload(shopUid, draft);
    // The ticket belongs to the session of this moment, also if the user opens another one before
    // the request ends.
    const sessionId = options.conversation.sessionId.value;
    draft.status = 'creating';
    let uid: string | null = null;
    try {
      const { result, error } = await options.createTicket(payload.shopUid, payload.title, payload.body);
      uid = error ? null : (result?.data?.uid ?? null);
    } catch {
      // useMyFetch throws for some failures, for example a 404 in its default error handler. Each
      // failure gives the same error state of the card.
    }
    if (uid === null) {
      draft.status = 'error';
      return;
    }
    if (sessionId) {
      try {
        await options.recordTicket(sessionId, uid);
      } catch {
        // The ticket exists. Only the badge of the history is missing.
      }
    }
    if (options.conversation.sessionId.value === sessionId) options.conversation.ticketUid.value = uid;
    draft.status = 'created';
    draft.ticketUid = uid;
  };

  /** The link card of the ticket of an opened session, or no card for a session without one. */
  const showTicket = (ticketUid: string | null) => {
    shown = null;
    card.value = ticketUid ? { status: 'created', title: '', summary: '', source: 'suge', ticketUid } : null;
  };

  const close = () => {
    if (card.value?.status !== 'creating') card.value = null;
  };

  return { card, allowed, openCard, proposalArrived, create, close, showTicket };
}
